import { Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";

const LINE_ITEM_TRANSITION_MS = 300;

type ScoreLineItemColor = "positive" | "negative" | "default";

interface ScoreLineItemProps {
  /**
   * Label text for the line item (e.g., "4-8h cancellation")
   */
  label: string;
  /**
   * Numeric value to display (e.g., -70 or +55)
   */
  value: number;
  /**
   * Color variant for the value text.
   * @default "default"
   */
  color?: ScoreLineItemColor;
  /**
   * Whether the line item is visible (controls opacity transition).
   */
  isVisible: boolean;
  /**
   * Optional icon to display next to the label or value text.
   */
  icon?: React.ReactNode;
  /**
   * Position of the icon relative to the line item content.
   * @default "left"
   */
  iconPosition?: "left" | "right";
  /**
   * When true, renders the value as-is (no +/- prefix) and uses neutral colors.
   * Used for the "Current Clipboard Score" baseline row.
   * @default false
   */
  isBaseline?: boolean;
  /**
   * When true, applies the value color to the label text as well.
   * @default false
   */
  colorLabel?: boolean;
}

function formatValue(value: number, isBaseline: boolean): string {
  if (isBaseline) {
    return `${value}`;
  }

  return value >= 0 ? `+${value}` : `${value}`;
}

function getValueColor(color: ScoreLineItemColor, isBaseline: boolean): (theme: Theme) => string {
  if (isBaseline) {
    return (theme) => theme.palette.text.primary;
  }

  return (theme) => {
    if (color === "positive") {
      return theme.palette.success.main;
    }

    if (color === "negative") {
      return theme.palette.intent?.error.text ?? theme.palette.error.main;
    }

    return theme.palette.text.primary;
  };
}

export function ScoreLineItem(props: ScoreLineItemProps) {
  const {
    label,
    value,
    color = "default",
    isVisible,
    icon,
    iconPosition = "left",
    isBaseline = false,
    colorLabel = false,
  } = props;

  return (
    <Stack
      aria-hidden={!isVisible}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
        transition: `opacity ${LINE_ITEM_TRANSITION_MS}ms ease-in-out`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {iconPosition === "left" && icon}
        <Text
          variant="body2"
          sx={(theme) => ({
            color: colorLabel
              ? getValueColor(color, isBaseline)(theme)
              : theme.palette.text.secondary,
          })}
        >
          {label}
        </Text>
        {iconPosition === "right" && icon}
      </Stack>
      <Text variant="body1" sx={(theme) => ({ color: getValueColor(color, isBaseline)(theme) })}>
        {formatValue(value, isBaseline)}
      </Text>
    </Stack>
  );
}
